/**
 * OmegaMonitorGiPage — proof that every Monitor PRODUCT component is a real GI.
 *
 * Founder-only. Reads /omega/monitor/product/proof and shows the four proofs:
 * (1) signed — recent atoms carry an Ed25519 signature; (2) typed — each is one
 * of the 8 Holosynthics kinds; (3) stored — counted in atom_tags.db under the
 * MonitorProduct vertical; (4) converged — all increment ONE product conductor.
 */

import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

interface ProductAtom {
  id: string;
  atom_kind: string;
  vertical: string;
  payload: string;
  signature_hex: string;
  public_key_hex: string;
  created_iso: string;
}
interface ProductProof {
  conductor_id: string;
  vertical: string;
  monitor_gi_hash: string;
  total_atoms: number;
  atoms_converged: number;
  by_kind: Record<string, number>;
  convergence_chain: string[];
  recent_atoms: ProductAtom[];
  note: string;
}

const PANEL: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: 16,
  marginBottom: 20,
};

function fmt(n: number): string {
  return n.toLocaleString('en-US');
}

export function OmegaMonitorGiPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const q = useQuery({
    queryKey: ['monitor', 'product', 'proof'],
    queryFn: () => apiFetch<ProductProof>('/omega/monitor/product/proof'),
    refetchInterval: 15_000,
  });

  const p = q.data;
  const kinds = p ? Object.entries(p.by_kind) : [];

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 16px 48px' }}>
      <Breadcrumbs crumbs={[{ label: 'OMEGA', to: '/omega' }, { label: 'Monitor GI' }]} />
      <h1 style={{ margin: '8px 0 4px', fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        Monitor — every component is a GI
      </h1>
      <p style={{ color: '#6b7280', marginTop: 0 }}>
        Each product event (signup, verdict, alert, report, upsell, referral, dashboard) is a
        signed Holosynthics atom in atom_tags.db, converging on ONE Monitor-product conductor.
      </p>

      {q.isPending && <p style={{ color: '#6b7280' }}>Loading proof…</p>}
      {q.isError && (
        <p style={{ color: '#b91c1c' }}>
          Couldn't load: {String((q.error as Error)?.message ?? q.error)}
        </p>
      )}

      {p && (
        <>
          {/* Proof 4 — convergence */}
          <section style={{ ...PANEL, background: '#f5f3ff', borderColor: '#ddd6fe' }}>
            <div style={{ fontSize: '9pt', fontWeight: 700, color: '#6d28d9', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Converged to ONE
            </div>
            <div style={{ fontSize: '26pt', fontWeight: 800, color: '#4c1d95', lineHeight: 1.1 }}>
              {fmt(p.atoms_converged)}
            </div>
            <div style={{ fontSize: '9.5pt', color: '#6b7280' }}>
              product atoms converged on conductor <code style={{ fontSize: '8.5pt' }}>{p.conductor_id.slice(0, 18)}…</code>{' '}
              (vertical <strong>{p.vertical}</strong>)
            </div>
          </section>

          {/* THE Monitor GI — one content-addressed hash */}
          <section style={{ ...PANEL, background: '#0f172a', borderColor: '#0f172a' }}>
            <div style={{ fontSize: '9pt', fontWeight: 700, color: '#a5b4fc', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              The Monitor product IS one GI
            </div>
            <div style={{ fontSize: '11pt', fontWeight: 800, color: '#fff', wordBreak: 'break-all', fontFamily: 'ui-monospace, monospace', marginTop: 6 }}>
              {p.monitor_gi_hash}
            </div>
            <div style={{ fontSize: '8.5pt', color: '#94a3b8', marginTop: 6 }}>
              SHA-256 over all {fmt(p.total_atoms)} sorted product-atom ids — the entire Monitor as ONE signed GI entity.
            </div>
            <div style={{ marginTop: 12 }}>
              {p.convergence_chain?.map((step, i) => (
                <div key={i} style={{ fontSize: '9pt', color: '#cbd5e1', padding: '2px 0', wordBreak: 'break-all' }}>
                  {i === 0 ? '' : '↓ '}{step}
                </div>
              ))}
            </div>
          </section>

          {/* Proofs 2+3 — typed + stored in atom_tags.db */}
          <section style={PANEL}>
            <div style={{ fontSize: '9pt', fontWeight: 700, color: '#6b7280', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>
              By Holosynthics kind (in atom_tags.db)
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10pt' }}>
              <tbody>
                {kinds.map(([kind, count]) => (
                  <tr key={kind} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 0' }}>{kind}</td>
                    <td style={{ padding: '6px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* Proof 1 — signed recent atoms */}
          <section style={PANEL}>
            <div style={{ fontSize: '9pt', fontWeight: 700, color: '#6b7280', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>
              Recent signed atoms
            </div>
            {p.recent_atoms.length === 0 ? (
              <p style={{ color: '#9ca3af', fontSize: '9.5pt' }}>
                No product atoms yet — exercise a client flow (signup / ingest / report …) and they appear here.
              </p>
            ) : (
              p.recent_atoms.map((a) => (
                <div key={a.id} style={{ borderTop: '1px solid #f1f5f9', padding: '8px 0', fontSize: '9pt' }}>
                  <div>
                    <span style={{ fontWeight: 700 }}>{a.atom_kind}</span>{' '}
                    <span style={{ color: '#9ca3af' }}>· {a.created_iso}</span>
                  </div>
                  <div style={{ color: '#374151', wordBreak: 'break-all' }}>{a.payload}</div>
                  <div style={{ color: '#9ca3af', fontSize: '8pt', wordBreak: 'break-all' }}>
                    sig {a.signature_hex.slice(0, 24)}… · key {a.public_key_hex.slice(0, 16)}…
                  </div>
                </div>
              ))
            )}
          </section>

          <p style={{ color: '#9ca3af', fontSize: '8.5pt' }}>{p.note}</p>
        </>
      )}
    </div>
  );
}
